<template>
  <div
    class="container-fluid dynamic-custom-form-shell"
    :class="{ 'dynamic-custom-form-shell--embedded': embedded }"
  >
    <LoadingOverlay
      :show="isSubmitting"
      message="Saving changes..."
      loading-text="Saving..."
    />
    <ContentSkeleton v-if="loading" variant="accordion" />

    <template v-else-if="ready">
      <div
        v-if="!embedded && !suppressChrome"
        class="d-flex align-items-center justify-content-between mb-2 mt-3"
      >
        <h4 class="mb-0">
          <h2>
            {{ isAddMode ? $t("New") : $t("Edit") }}
            {{
              selectedModelData.label
                ? $t(selectedModelData.label)
                : $t(selectedModelData.modelName)
            }}
          </h2>
          <span v-if="!isAddMode && form.idModel">{{ form.name }}</span>
        </h4>
        <button type="button" class="btn btn-primary btn-sm" @click="goBack">
          <i class="fas fa-arrow-left"></i> {{ $t("Back") || "Back" }}
        </button>
      </div>

      <div
        class="card container-form"
        :class="{ 'border-0 shadow-none': embedded || suppressChrome }"
      >
        <div class="card-body" :class="{ 'p-0 p-md-1': embedded }">
          <component
            :is="formComponent"
            ref="customForm"
            :key="formRenderKey"
            :fields="modelFields"
            :value="form"
            :lists="lists"
            :visual-groups="selectedModelData.visualGroups || []"
            :selected-model="selectedModel"
            :model-definitions="modelDefinitions"
            :models="models"
            :save-in-progress="isSubmitting"
            :embedded="embedded"
            @input="onFormInput"
            @submit="onSubmit"
            @cancel="onCancel"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";
import { wencesApi } from "@/api/wencesApi";
import { promptFindTeuTrackingOnSave } from "@/utils/findTeuPromptTrackingOnSave";
import Swal from "sweetalert2";
import {
  getApiErrorData,
  isHookValidationError,
  promptHookValidationForceSave,
  showForceSaveRetryError,
} from "@/utils/dynamicModelValidationSwal";
import { resolveDynamicFormComponent } from "@/utils/dynamicFormRegistry";
import { prepareCustomFormSavePayload } from "@/utils/dynamicModelCustomFormPayload";
import { MONGODB_OBJECT_ID_RE, resolveDynamicModelRouteParams } from "@/utils/dynamicModelPaths";
import { copyTranslatableSiblingsInto } from "@/utils/fieldValueI18n";
import offerNotaComercialOnOperationCreateMixin from "@/modules/nota_comercial_module/mixins/offerNotaComercialOnOperationCreateMixin";

export default {
  name: "DynamicModelCustomFormShell",
  mixins: [offerNotaComercialOnOperationCreateMixin],
  components: { LoadingOverlay, ContentSkeleton },
  props: {
    mode: {
      type: String,
      required: true,
      validator: (v) => v === "add" || v === "edit",
    },
    embedded: { type: Boolean, default: false },
    embedModelName: { type: String, default: "" },
    embedItemId: { type: String, default: "" },
    /** Prefill add (p. ej. calendario) */
    initialStartDate: { type: String, default: "" },
    /** En detalle custom: el padre ya muestra título y gt-model-actions-toolbar */
    suppressChrome: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: true,
      ready: false,
      formRenderKey: 0,
      models: [],
      modelDefinitions: {},
      selectedModel: "",
      selectedModelData: null,
      modelFields: [],
      formMeta: {},
      formSchemaDefaults: {},
      form: {},
      lists: [],
      formComponent: null,
      isSubmitting: false,
      lockInterval: null,
    };
  },
  computed: {
    routeModelName() {
      if (this.embedded && this.embedModelName) return this.embedModelName;
      const { modelName } = resolveDynamicModelRouteParams(
        this.$route,
        (this.models || []).map((m) => m.modelName)
      );
      return modelName || this.$route.params.modelName || "";
    },
    routeItemId() {
      if (this.embedded && this.embedItemId) return this.embedItemId;
      const { itemId } = resolveDynamicModelRouteParams(
        this.$route,
        (this.models || []).map((m) => m.modelName)
      );
      return itemId || this.$route.params.itemId || "";
    },
    isAddMode() {
      if (this.mode === "add") return true;
      if (this.mode === "edit") return false;
      const path = String(this.$route?.path || "");
      return /\/add\/?$/.test(path) || path.includes("/add?");
    },
  },
  watch: {
    routeModelName: {
      immediate: true,
      handler(name) {
        if (!name) return;
        this.bootstrap();
      },
    },
    routeItemId() {
      if (this.ready && !this.isAddMode) {
        this.loadItem(this.routeItemId);
      }
    },
  },
  async mounted() {
    await this.fetchLists();
    if (!this.isAddMode && this.routeItemId) {
      await this.acquireLock();
    }
  },
  beforeDestroy() {
    if (this.lockInterval) clearInterval(this.lockInterval);
    if (!this.isAddMode && this.routeItemId) {
      this.releaseLock();
    }
  },
  methods: {
    async bootstrap() {
      this.loading = true;
      this.ready = false;
      try {
        await this.fetchModels();
        await this.loadFormSchema();
        if (!this.formComponent) {
          console.error(
            "[DynamicModelCustomFormShell] formulario autónomo sin componente registrado"
          );
          return;
        }
        if (this.isAddMode) {
          this.resetForm();
          this.applyFormSchemaDefaults();
          this.applyRouteQueryPrefill();
        } else {
          await this.loadItem(this.routeItemId);
        }
        this.formRenderKey += 1;
        this.ready = true;
      } catch (e) {
        console.error("[DynamicModelCustomFormShell] bootstrap", e);
      } finally {
        this.loading = false;
      }
    },
    async fetchModels() {
      const { data } = await wencesApi.get("/api/gotrading/model-definitions");
      this.models = data;
      const defs = {};
      for (const m of data) {
        defs[m.modelName] = m;
      }
      this.modelDefinitions = defs;
    },
    async fetchLists() {
      const { data } = await wencesApi.get("/api/gotrading/lists");
      this.lists = data;
    },
    async loadFormSchema() {
      const params = { mode: this.isAddMode ? "add" : "edit" };
      if (!this.isAddMode && this.routeItemId) {
        params.itemId = this.routeItemId;
      }
      const { data } = await wencesApi.get(
        `/api/gotrading/dynamic/${this.routeModelName}/form-schema`,
        { params }
      );
      this.selectedModel = data.modelName;
      const modelFromList = this.models.find(
        (m) => m.modelName.toLowerCase() === this.selectedModel.toLowerCase()
      );
      this.selectedModelData = {
        ...(modelFromList || {}),
        modelName: data.modelName,
        label: data.label,
        autoSave: data.autoSave,
        visualGroups: data.visualGroups,
        fields: data.fields,
      };
      this.modelFields = data.fields;
      this.formMeta = data.meta || {};
      this.formSchemaDefaults = data.formDefaults || {};
      this.formComponent = await resolveDynamicFormComponent(
        this.selectedModel,
        this.isAddMode ? "add" : "edit",
        {
          customFormKey: this.formMeta?.customFormKey || null,
          trustCustomFormKey: true,
        }
      );
    },
    resetForm() {
      const defaultForm = {};
      this.modelFields.forEach((field) => {
        if (field.isArray) {
          defaultForm[field.key] = [];
        } else if (field.type === "group") {
          defaultForm[field.key] = field.isArray ? [] : {};
        } else {
          defaultForm[field.key] = field.type === "number" ? 0 : "";
        }
      });
      this.form = defaultForm;
    },
    applyFormSchemaDefaults() {
      if (!this.formSchemaDefaults || typeof this.formSchemaDefaults !== "object") {
        return;
      }
      const defaults = JSON.parse(JSON.stringify(this.formSchemaDefaults));
      const next = { ...this.form };
      // FormV2 + visualGroups: anidar defaults planos en group_<name>
      (this.modelFields || []).forEach((field) => {
        if (!field?.key || !field.visualGroup) return;
        if (!Object.prototype.hasOwnProperty.call(defaults, field.key)) return;
        const gk = `group_${field.visualGroup}`;
        if (!defaults[gk] || typeof defaults[gk] !== "object") defaults[gk] = {};
        if (
          defaults[gk][field.key] === undefined ||
          defaults[gk][field.key] === null ||
          defaults[gk][field.key] === ""
        ) {
          defaults[gk][field.key] = defaults[field.key];
        }
        delete defaults[field.key];
      });
      Object.keys(defaults).forEach((key) => {
        if (key.startsWith("group_") && defaults[key] && typeof defaults[key] === "object") {
          next[key] = {
            ...(next[key] && typeof next[key] === "object" ? next[key] : {}),
            ...defaults[key],
          };
        } else {
          next[key] = defaults[key];
        }
      });
      this.form = next;
    },
    applyRouteQueryPrefill() {
      const q = this.$route.query || {};
      if (q.startDate && String(q.startDate).trim() && "startDate" in this.form) {
        this.form = { ...this.form, startDate: String(q.startDate).trim() };
      }
      if (this.initialStartDate && "startDate" in this.form) {
        this.form = { ...this.form, startDate: this.initialStartDate };
      }
    },
    async loadItem(itemId) {
      if (!itemId) return;
      const model = this.selectedModel || this.routeModelName;
      if (!model) return;
      const { data } = await wencesApi.get(
        `/api/gotrading/dynamic/${model}/${itemId}`
      );
      if (String(itemId) !== String(this.routeItemId)) return;
      this.form = this.nestItemIntoVisualGroups(data);
    },
    /**
     * FormV2 lee campos bajo group_<visualGroup>.*; la API trae plano.
     * Misma lógica que fetchItemPreload del edit clásico.
     *
     * Los campos `group` quedan FUERA del bucket: los edita el formulario custom
     * en la raíz (p. ej. ContratoForm con ENVIOS / POSICIONES / factura), no FormV2.
     * Si se copiaran aquí, el bucket sería una foto del GET que nadie actualiza y
     * flattenVisualGroupBuckets la volcaría sobre la raíz al guardar, perdiendo la edición.
     */
    nestItemIntoVisualGroups(item) {
      const formData = { ...(item || {}) };
      const visualGroups = this.selectedModelData?.visualGroups || [];
      if (!visualGroups.length) return formData;
      visualGroups.forEach((group) => {
        const groupKey = `group_${group.name}`;
        formData[groupKey] = {};
        (this.modelFields || []).forEach((field) => {
          if (field.type === "group") return;
          if (field.visualGroup === group.name && formData[field.key] !== undefined) {
            formData[groupKey][field.key] = formData[field.key];
            copyTranslatableSiblingsInto(formData[groupKey], formData, field);
          }
        });
      });
      return formData;
    },
    /** Recarga ítem (p. ej. botón Actualizar del detalle). */
    async reloadItem() {
      if (this.isAddMode || !this.routeItemId) return;
      await this.loadItem(this.routeItemId);
      this.formRenderKey += 1;
    },
    onFormInput(nextForm) {
      if (!nextForm || typeof nextForm !== "object") return;
      const next = JSON.parse(JSON.stringify(nextForm));
      this.syncVisualGroupBucketsFromRoot(next, this.form);
      this.form = next;
      this.maybeAskNotaComercialOnContactSelect(this.form);
    },
    /**
     * El formulario custom edita en la raíz; los buckets group_<visualGroup> que armó
     * nestItemIntoVisualGroups son la foto del GET y nadie los actualiza. Al guardar,
     * flattenVisualGroupBuckets vuelca el bucket sobre la raíz y la edición se perdía
     * en silencio (p. ej. Contrato.empresaAdministracion volvía al valor original).
     *
     * Acá se replica en el bucket el valor de cada campo que el custom form cambió en
     * la raíz. Solo los que cambiaron: si el editor real es FormV2 (que escribe dentro
     * del bucket y deja la raíz quieta), no se toca nada.
     */
    syncVisualGroupBucketsFromRoot(next, prev) {
      const visualGroups = this.selectedModelData?.visualGroups || [];
      if (!visualGroups.length || !prev || typeof prev !== "object") return;
      const groupNames = new Set(visualGroups.map((g) => String(g.name)));
      (this.modelFields || []).forEach((field) => {
        if (!field?.key || field.type === "group") return;
        if (!field.visualGroup || !groupNames.has(String(field.visualGroup))) return;
        const bucketKey = `group_${field.visualGroup}`;
        const bucket = next[bucketKey];
        if (!bucket || typeof bucket !== "object" || Array.isArray(bucket)) return;
        if (!Object.prototype.hasOwnProperty.call(bucket, field.key)) return;
        const nextVal = next[field.key];
        if (JSON.stringify(nextVal) === JSON.stringify(prev[field.key])) return;
        bucket[field.key] = nextVal;
        copyTranslatableSiblingsInto(bucket, next, field);
      });
    },
    buildPayload(rawForm) {
      return prepareCustomFormSavePayload(rawForm, this.modelFields);
    },
    hasValidItemId(id) {
      return MONGODB_OBJECT_ID_RE.test(String(id || "").trim());
    },
    /** Modo «Preguntar» del mapeo FindTeu: ofrece activar el seguimiento tras un guardado manual. */
    async promptFindTeuTracking(modelName, itemId) {
      await promptFindTeuTrackingOnSave({
        modelName,
        itemId,
        t: (key, params) => this.$t(key, params),
      });
    },
    async onSubmit(formFromChild) {
      if (this.isSubmitting) return;
      let body;
      try {
        const raw =
          formFromChild && typeof formFromChild === "object"
            ? formFromChild
            : this.form;
        const payload = this.buildPayload(raw);
        body = { ...payload, saveTrigger: "manual" };
      } catch (prepErr) {
        console.error("[DynamicModelCustomFormShell] prepare payload", prepErr);
        await Swal.fire({
          icon: "error",
          title: this.$tn("Error") || "Error",
          text: prepErr?.message || "Error al preparar el formulario",
          confirmButtonText: "OK",
        });
        return;
      }

      // Sin ObjectId válido → siempre POST (alta). Nunca PUT a …/model/ (fallthrough).
      const itemId = this.routeItemId;
      const canUpdate = this.hasValidItemId(itemId);

      this.isSubmitting = true;
      try {
        if (!canUpdate) {
          const model = this.selectedModel || this.routeModelName;
          if (!model) {
            throw new Error("Modelo no seleccionado");
          }
          const { data } = await wencesApi.post(
            `/api/gotrading/dynamic/${model}`,
            body
          );
          if (data?._operationMessages?.length) {
            for (const msg of data._operationMessages) {
              await Swal.fire({
                icon: msg.type === "success" ? "success" : "info",
                title: msg.type === "success" ? "Éxito" : "Información",
                text: msg.message,
                confirmButtonText: "Entendido",
              });
            }
          }
          const savedId = data?._id;
          if (savedId) {
            await this.applyPendingNotaComercialOffer(savedId);
            await this.promptFindTeuTracking(model, savedId);
          }
          if (!this.embedded && savedId) {
            this.$router.push(`/dynamic-model/${model}/view/${savedId}`);
          } else if (savedId) {
            this.$emit("embedded-save", data);
          }
        } else {
          const model = this.selectedModel || this.routeModelName;
          await wencesApi.put(
            `/api/gotrading/dynamic/${model}/${itemId}`,
            body
          );
          await this.applyPendingNotaComercialOffer(itemId);
          await this.promptFindTeuTracking(model, itemId);
          if (this.embedded) {
            this.$emit("embedded-save");
          } else {
            await this.releaseLock();
            this.$router.push(`/dynamic-model/${model}/view/${itemId}`);
          }
        }
      } catch (error) {
        this.isSubmitting = false;
        await this.$nextTick();
        await this.handleSaveError(error, body);
      } finally {
        this.isSubmitting = false;
      }
    },
    async handleSaveError(error, lastBody) {
      const errorData = getApiErrorData(error);
      if (errorData && isHookValidationError(errorData)) {
        const confirmed = await promptHookValidationForceSave(
          (key, params) => this.$t(key, params),
          errorData.message,
          { modelName: this.selectedModel }
        );
        if (confirmed) {
          this.isSubmitting = true;
          try {
            const forced = { ...lastBody, forceSave: true };
            const itemId = this.routeItemId;
            const canUpdate = this.hasValidItemId(itemId);
            const model = this.selectedModel || this.routeModelName;
            let savedId = itemId;
            if (!canUpdate) {
              const { data } = await wencesApi.post(
                `/api/gotrading/dynamic/${model}`,
                forced
              );
              savedId = data?._id;
            } else {
              await wencesApi.put(
                `/api/gotrading/dynamic/${model}/${itemId}`,
                forced
              );
            }
            if (savedId) {
              await this.promptFindTeuTracking(model, savedId);
            }
            if (!this.embedded) {
              if (!canUpdate) {
                this.$router.push(
                  savedId
                    ? `/dynamic-model/${model}/view/${savedId}`
                    : `/dynamic-model/${model}`
                );
              } else {
                await this.releaseLock();
                this.$router.push(`/dynamic-model/${model}/view/${itemId}`);
              }
            } else {
              this.$emit("embedded-save");
            }
          } catch (retryError) {
            console.error("[DynamicModelCustomFormShell] forceSave", retryError);
            await showForceSaveRetryError(
              (key, params) => this.$t(key, params),
              retryError
            );
          } finally {
            this.isSubmitting = false;
          }
        }
        return;
      }
      console.error("[DynamicModelCustomFormShell] save", error);
      await Swal.fire({
        icon: "error",
        title: this.$tn("Error") || "Error",
        text:
          errorData?.message ||
          errorData?.error ||
          this.$tn("An unexpected error occurred") ||
          "Error al guardar",
        confirmButtonText: "OK",
      });
    },
    onCancel() {
      this.goBack();
    },
    goBack() {
      if (this.embedded) {
        this.$emit("cancel");
        return;
      }
      if (!this.isAddMode) this.releaseLock();
      const model = this.selectedModel || this.routeModelName;
      if (this.isAddMode) {
        this.$router.push({ name: "DynamicModel", params: { modelName: model } });
      } else {
        this.$router.push(`/dynamic-model/${model}/view/${this.routeItemId}`);
      }
    },
    async acquireLock() {
      try {
        await wencesApi.post(
          `/api/gotrading/edit-lock/${this.selectedModel}/${this.routeItemId}/acquire`
        );
        this.lockInterval = setInterval(() => {
          wencesApi.post(
            `/api/gotrading/edit-lock/${this.selectedModel}/${this.routeItemId}/activity`
          );
        }, 30000);
      } catch (error) {
        if (error.response?.status === 409) {
          const lock = error.response.data.lock;
          await Swal.fire({
            title: "Otro usuario esta editando este registro",
            html: `<p><strong>Usuario:</strong> ${lock.userName}</p>`,
            icon: "warning",
            confirmButtonText: "Entendido",
          });
          if (!this.embedded) {
            this.$router.push({
              name: "DynamicModelDetail",
              params: {
                modelName: this.selectedModel,
                itemId: this.routeItemId,
              },
            });
          }
        }
      }
    },
    async releaseLock() {
      try {
        await wencesApi.post(
          `/api/gotrading/edit-lock/${this.selectedModel}/${this.routeItemId}/release`
        );
        if (this.lockInterval) {
          clearInterval(this.lockInterval);
          this.lockInterval = null;
        }
      } catch (e) {
        console.error("[DynamicModelCustomFormShell] releaseLock", e);
      }
    },
  },
};
</script>

<style scoped>
.dynamic-custom-form-shell--embedded {
  max-width: 100%;
  padding-left: 0;
  padding-right: 0;
}
</style>
